// CSR-1953 — Record Update math, shared by the client (live modal preview) and
// the server (authoritative premium/status written on save). One
// implementation so the preview and the persisted values never drift, à la
// shared/premium.ts. Pure and side-effect free: no I/O, no server-only imports,
// so the client can import it.

import {
  computeTotalPremium,
  exceedsPremiumLimit,
  sumMoney,
  type PremiumBreakdown,
} from "@shared/premium";

export type RecordUpdateType =
  | "positive_endorsement"
  | "negative_endorsement"
  | "cancellation"
  | "reinstatement";

// The `billing_type` values on a record update, with their display labels.
export type BillingType =
  | "direct_bill"
  | "agency_bill_outside_financing"
  | "agency_bill_paid_in_full";

export const BILLING_TYPE_LABELS: Record<BillingType, string> = {
  direct_bill: "Direct Bill",
  agency_bill_outside_financing: "Agency Bill — Outside Financing",
  agency_bill_paid_in_full: "Agency Bill — Paid in Full",
};

/** Human label for a stored billing type; unknown/blank values pass through. */
export function formatBillingType(
  value: string | null | undefined,
): string | null {
  if (!value) return null;
  return BILLING_TYPE_LABELS[value as BillingType] ?? value;
}

// Update types that ADD premium; the rest subtract it. The caller always enters
// a positive premium — this module applies the sign from the type.
const PREMIUM_ADDING_TYPES: ReadonlySet<RecordUpdateType> = new Set<RecordUpdateType>([
  "positive_endorsement",
  "reinstatement",
]);

export interface RecordUpdateInput {
  type: RecordUpdateType;
  /** The policy's current premium breakdown, as read from the Data Center. */
  currentPremium: PremiumBreakdown;
  /** Entered premium magnitude (always positive per the modal contract). */
  premium: string | number;
  brokerFee?: string | number | null;
  /** MGA fee entered on the update; adds onto the policy's `policyFees`. */
  mgaFee?: string | number | null;
  taxes?: string | number | null;
  /** Informational only — never affects the Total. */
  agencyCommissionPct?: string | number | null;
  /** Informational only — never affects the Total. */
  agentCommissionPct?: string | number | null;
  /** Drives cancellation status/date. Defaults to `today` when omitted. */
  effectiveDate?: string;
  /** Injectable clock for status resolution; defaults to the current date. */
  today?: string | Date;
}

export interface RecordUpdateNewBreakdown {
  policyPremium: string | null;
  brokerFee: string | null;
  policyFees: string | null;
  taxes: string | null;
}

export interface PolicyStatusChange {
  status: string;
  /** The new cancellation date; `null` clears it (reinstatement). */
  cancellationDate: string | null;
}

export interface RecordUpdateResult {
  /** Signed premium applied to the policy premium (e.g. "-1000.00"). */
  premiumDelta: string;
  /** New absolute breakdown forwarded to the Data Center. */
  newBreakdown: RecordUpdateNewBreakdown;
  /** Total Outstanding Premium — equals the new policy premium (may be negative). */
  totalOutstandingPremium: string | null;
  /** Total Non-Premium — new broker fee + finance charge (`policyFees`) + taxes. */
  totalNonPremium: string;
  /** Full new premium; may be negative after a cancellation. */
  total: string;
  /** True when the new Total overflows the numeric(10,2) premium column. */
  exceedsLimit: boolean;
  /** Informational agency commission amount (signed); does NOT affect the Total. */
  agencyCommissionAmount: string;
  /** Informational agent commission amount (signed); does NOT affect the Total. */
  agentCommissionAmount: string;
  /** New policy status, or `null` when the type does not change status. */
  status: string | null;
  /**
   * `undefined` = leave the policy's cancellation date untouched (endorsements),
   * `null` = clear it (reinstatement), a string = set it (cancellation).
   */
  cancellationDate: string | null | undefined;
}

// Exported so the timeline card signs premium/commissions like the calc module.
export function recordUpdatePremiumIsAdded(type: RecordUpdateType): boolean {
  return PREMIUM_ADDING_TYPES.has(type);
}

/** A summed component is "" only when every input was blank; forward that as null. */
function blankToNull(value: string): string | null {
  return value === "" ? null : value;
}

// CSR-1987 — a cancellation SUBTRACTS the entered fees from the policy's existing
// fees (`resulting = existing − entered`), mirroring how the entered premium
// already reduces the premium; the result may go negative. The other three types
// keep ADDING entered fees. Negate a present entered fee for a cancellation while
// preserving blank/absent inputs so `blankToNull` still nulls an untouched fee.
function signedEnteredFee(
  value: string | number | null | undefined,
  subtract: boolean,
): string | number | null | undefined {
  if (!subtract) return value;
  if (value === null || value === undefined || value === "") return value;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? -numeric : value;
}

/**
 * True when a total's magnitude overflows the numeric(10,2) premium column.
 * Reuses `exceedsPremiumLimit` on the absolute value so a large NEGATIVE total
 * (valid after a cancellation) is still caught — the base helper only guards the
 * positive bound.
 */
function overflowsMoneyColumn(total: string): boolean {
  if (total === "") return false;
  return exceedsPremiumLimit(Math.abs(Number(total)).toString());
}

/** Calendar day as a comparable YYYYMMDD integer, ignoring time and timezone. */
function calendarDay(value: string | Date): number {
  if (typeof value === "string") {
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      return Number(match[1]) * 10000 + Number(match[2]) * 100 + Number(match[3]);
    }
    const parsed = new Date(value);
    return (
      parsed.getFullYear() * 10000 +
      (parsed.getMonth() + 1) * 100 +
      parsed.getDate()
    );
  }
  return value.getFullYear() * 10000 + (value.getMonth() + 1) * 100 + value.getDate();
}

/**
 * The status and cancellation-date transition for a record update, or `null`
 * when the type leaves the policy status unchanged (endorsements).
 * - cancellation, future effective date → "pending cancel"; today/past → "cancelled";
 *   cancellation date = effective date.
 * - reinstatement → "active" and clears the cancellation date.
 */
export function resolvePolicyStatusChange(
  type: RecordUpdateType,
  effectiveDate: string,
  today: string | Date = new Date(),
): PolicyStatusChange | null {
  if (type === "reinstatement") {
    return { status: "active", cancellationDate: null };
  }
  if (type === "cancellation") {
    const isFuture = calendarDay(effectiveDate) > calendarDay(today);
    return {
      status: isFuture ? "pending cancel" : "cancelled",
      cancellationDate: effectiveDate,
    };
  }
  return null;
}

function signedCommission(
  premiumMagnitude: number,
  premiumIsAdded: boolean,
  percentage: string | number | null | undefined,
): string {
  const pct = Number(percentage);
  if (!Number.isFinite(pct) || pct === 0) return "0.00";
  const amount = (premiumMagnitude * pct) / 100;
  return sumMoney([premiumIsAdded ? amount : -amount]);
}

// Signed commission amounts for the timeline card, reusing signedCommission.
export function recordUpdateCommissionAmounts(
  type: RecordUpdateType,
  premium: string | number,
  agencyCommissionPct: string | number | null | undefined,
  agentCommissionPct: string | number | null | undefined,
): { agency: string; agent: string } {
  const magnitude = Math.abs(Number(premium) || 0);
  const added = recordUpdatePremiumIsAdded(type);
  return {
    agency: signedCommission(magnitude, added, agencyCommissionPct),
    agent: signedCommission(magnitude, added, agentCommissionPct),
  };
}

export function computeRecordUpdate(
  input: RecordUpdateInput,
): RecordUpdateResult {
  const { type, currentPremium } = input;
  const premiumIsAdded = recordUpdatePremiumIsAdded(type);
  const premiumMagnitude = Math.abs(Number(input.premium) || 0);
  const signedPremium = premiumIsAdded ? premiumMagnitude : -premiumMagnitude;

  const premiumDelta = sumMoney([signedPremium]);

  const feesAreSubtracted = type === "cancellation";
  const newBreakdown: RecordUpdateNewBreakdown = {
    policyPremium: blankToNull(
      sumMoney([currentPremium.policyPremium, signedPremium]),
    ),
    brokerFee: blankToNull(
      sumMoney([
        currentPremium.brokerFee,
        signedEnteredFee(input.brokerFee, feesAreSubtracted),
      ]),
    ),
    policyFees: blankToNull(
      sumMoney([
        currentPremium.policyFees,
        signedEnteredFee(input.mgaFee, feesAreSubtracted),
      ]),
    ),
    taxes: blankToNull(
      sumMoney([
        currentPremium.taxes,
        signedEnteredFee(input.taxes, feesAreSubtracted),
      ]),
    ),
  };

  const total = computeTotalPremium(newBreakdown);
  const totalNonPremium = sumMoney([
    newBreakdown.brokerFee,
    newBreakdown.policyFees,
    newBreakdown.taxes,
  ]);

  const statusChange = resolvePolicyStatusChange(
    type,
    input.effectiveDate ?? dateToString(input.today ?? new Date()),
    input.today,
  );

  return {
    premiumDelta,
    newBreakdown,
    totalOutstandingPremium: newBreakdown.policyPremium,
    totalNonPremium,
    total,
    exceedsLimit: overflowsMoneyColumn(total),
    agencyCommissionAmount: signedCommission(
      premiumMagnitude,
      premiumIsAdded,
      input.agencyCommissionPct,
    ),
    agentCommissionAmount: signedCommission(
      premiumMagnitude,
      premiumIsAdded,
      input.agentCommissionPct,
    ),
    status: statusChange ? statusChange.status : null,
    cancellationDate: statusChange ? statusChange.cancellationDate : undefined,
  };
}

function dateToString(value: string | Date): string {
  if (typeof value === "string") return value;
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

// CSR-1971 / CSR-1967 — the wire body the client POSTs to the AMS thin proxy,
// which forwards it verbatim (plus a server-set actor/authorDisplayName) to DC's
// POST /api/external/policies/:id/record-updates. That DC schema
// (`recordPolicyUpdateRequestSchema`) is `.strict()` and re-validates: every
// money field must be a decimal string (never ""), and the breakdown components
// must sum to `breakdown.premium`. Building the body here — next to
// `computeRecordUpdate`, whose result it serializes — keeps the live modal
// preview and the persisted values from ever drifting.

export interface RecordUpdateBreakdownBody {
  policyPremium: string;
  brokerFee: string;
  policyFees: string;
  taxes: string;
  premium: string;
}

export interface RecordUpdateRequestBody {
  type: RecordUpdateType;
  billingType: string | null;
  // CSR-2340 — the carrier's endorsement number ("END #"); free text, optional.
  // Must be spelled exactly `endorsementNumber`: DC's schema is `.strict()`.
  endorsementNumber: string | null;
  premium: string;
  brokerFee: string;
  mgaFee: string;
  taxes: string;
  agencyCommissionPct: string;
  agentCommissionPct: string;
  effectiveDate: string;
  resultingPremium: string;
  totalOutstandingPremium: string;
  totalNonPremium: string;
  resultingStatus: string | null;
  breakdown: RecordUpdateBreakdownBody;
  // Lifecycle-only (cancellation / reinstatement). Omitted for endorsements —
  // DC rejects a non-null status/cancellationDate on a non-lifecycle update.
  status?: string | null;
  cancellationDate?: string | null;
}

/** The entered form values a record-update request is built from. */
export interface RecordUpdateRequestInput {
  type: RecordUpdateType;
  billingType?: string | null;
  endorsementNumber?: string | null;
  premium: string | number;
  brokerFee?: string | number | null;
  mgaFee?: string | number | null;
  taxes?: string | number | null;
  agencyCommissionPct?: string | number | null;
  agentCommissionPct?: string | number | null;
  effectiveDate: string;
}

// A blank/absent/non-numeric money or percent component becomes "0": DC's
// decimal-string validator rejects "" (and a stray "." a sanitized field can
// still produce), and a "0" keeps the breakdown summing to the premium.
function amountOrZero(value: string | number | null | undefined): string {
  const text = (value ?? "").toString().trim();
  if (text === "" || !Number.isFinite(Number(text))) return "0";
  return text;
}

/**
 * Serialize the entered form values + the computed result into the DC
 * record-update request body. `status`/`cancellationDate` are included ONLY when
 * the update changes the policy lifecycle (cancellation / reinstatement).
 */
export function buildRecordUpdateRequestBody(
  input: RecordUpdateRequestInput,
  result: RecordUpdateResult,
): RecordUpdateRequestBody {
  const body: RecordUpdateRequestBody = {
    type: input.type,
    billingType: input.billingType ?? null,
    endorsementNumber: input.endorsementNumber?.trim() || null,
    premium: amountOrZero(input.premium),
    brokerFee: amountOrZero(input.brokerFee),
    mgaFee: amountOrZero(input.mgaFee),
    taxes: amountOrZero(input.taxes),
    agencyCommissionPct: amountOrZero(input.agencyCommissionPct),
    agentCommissionPct: amountOrZero(input.agentCommissionPct),
    effectiveDate: input.effectiveDate,
    resultingPremium: amountOrZero(result.total),
    totalOutstandingPremium: amountOrZero(result.totalOutstandingPremium),
    totalNonPremium: amountOrZero(result.totalNonPremium),
    resultingStatus: result.status,
    breakdown: {
      policyPremium: amountOrZero(result.newBreakdown.policyPremium),
      brokerFee: amountOrZero(result.newBreakdown.brokerFee),
      policyFees: amountOrZero(result.newBreakdown.policyFees),
      taxes: amountOrZero(result.newBreakdown.taxes),
      premium: amountOrZero(result.total),
    },
  };

  if (result.status != null) {
    body.status = result.status;
    body.cancellationDate = result.cancellationDate ?? null;
  }

  return body;
}

// CSR-1955 — a record-update row read back from DC's feed (CSR-1966) for the
// timelines. Money fields are decimal strings (may be negative after a cancel).
export interface PolicyRecordUpdateRow {
  id: string;
  type: RecordUpdateType;
  billingType: string | null;
  endorsementNumber: string | null;
  premium: string;
  brokerFee: string;
  mgaFee: string;
  taxes: string;
  agencyCommissionPct: string;
  agentCommissionPct: string;
  effectiveDate: string;
  resultingPremium: string;
  totalOutstandingPremium: string;
  totalNonPremium: string;
  resultingStatus: string | null;
  authorDisplayName: string | null;
  createdAt: string;
}
